/**
 * Pure, deterministic helpers shared by the block-board and rules-board game
 * engines. Nothing here touches `this`, createjs, or the DOM, so every
 * function can be unit-tested directly with plain inputs and (where
 * randomness is involved) an injected RNG.
 */

/**
 * Converts a mouse position local to the board (in pixels) into a grid
 * cell address.
 *
 * Replaces the hardcoded-pixel division logic in `blockBoardEngine`'s
 * `getMouseCoordinates` (145px row height / 135px col width, optionally
 * capped to a max row/col of 3) and the equivalent per-game
 * `getMouseCoordinates` config hooks in `rulesBoardEngine` (which use
 * their own cell sizes, e.g. 183px, and cap to a max row/col of 2).
 *
 * @param localX - The x position, in pixels, local to the board's origin.
 * @param localY - The y position, in pixels, local to the board's origin.
 * @param opts.cellWidth - The pixel width of one grid column.
 * @param opts.cellHeight - The pixel height of one grid row.
 * @param opts.maxRow - When provided, the resulting row is clamped to at
 * most this value (mirrors the `Math.min`/manual-clamp capping seen at both
 * call sites). Leave `undefined` to not cap.
 * @param opts.maxCol - Same as `maxRow`, but for the resulting column.
 * @returns The zero-based `{ row, col }` grid cell under the point.
 */
export function mouseToCell(
  localX: number,
  localY: number,
  opts: {
    cellWidth: number;
    cellHeight: number;
    maxRow?: number;
    maxCol?: number;
  },
): { row: number; col: number } {
  const { cellWidth, cellHeight, maxRow, maxCol } = opts;
  let row = Math.floor(localY / cellHeight);
  let col = Math.floor(localX / cellWidth);
  if (maxRow !== undefined) {
    row = Math.min(maxRow, row);
  }
  if (maxCol !== undefined) {
    col = Math.min(maxCol, col);
  }
  return { row, col };
}

/**
 * Picks `count` distinct indices from the range `[0, poolSize)`, optionally
 * avoiding a set of already-used indices, using an injected RNG.
 *
 * Replaces the naive "pick a random index, re-roll while it collides with an
 * already-chosen one" loops used to fill the answer tiles in both
 * `blockBoardEngine.nextProblem` (the `chosen`/`m` loop building `selected`)
 * and `rulesBoardEngine.nextProblem` (the `blnChosen`/`m` loop). Those loops
 * are correct but can spin arbitrarily long as the pool fills up; this
 * implementation instead does a partial Fisher-Yates shuffle of the
 * candidate index pool and slices off the first `count` entries, which is
 * both faster and easier to reason about/test.
 *
 * @param poolSize - The number of candidate indices available, i.e. indices
 * are drawn from `[0, poolSize)`.
 * @param count - How many distinct indices to return. Must be no greater
 * than the number of indices remaining after `exclude` is applied.
 * @param rng - A function returning a float in `[0, 1)`, used to drive the
 * shuffle (inject `Math.random`, a seeded generator, or a
 * `Chance`-backed closure for deterministic tests).
 * @param exclude - Indices that must not appear in the result.
 * @returns An array of `count` distinct indices, in random order.
 */
export function pickDistinctIndices(
  poolSize: number,
  count: number,
  rng: () => number,
  exclude?: ReadonlySet<number> | Iterable<number>,
): number[] {
  const excludeSet =
    exclude instanceof Set ? exclude : new Set<number>(exclude ?? []);

  const pool: number[] = [];
  for (let i = 0; i < poolSize; i++) {
    if (!excludeSet.has(i)) {
      pool.push(i);
    }
  }

  if (count > pool.length) {
    throw new RangeError(
      `Cannot pick ${count} distinct indices from a pool of ${pool.length} available (poolSize=${poolSize}, excluded=${excludeSet.size}).`,
    );
  }

  // Partial Fisher-Yates: shuffle only as many positions as we need to take.
  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(rng() * (pool.length - i));
    const tmp = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = tmp;
  }

  return pool.slice(0, count);
}

/**
 * Returns a random permutation of `values` such that no element remains at
 * its original index, retrying the shuffle until that holds (or until
 * `maxAttempts` is exhausted).
 *
 * Replaces the second "reshuffle the just-chosen answers" loop in both
 * engines' `nextProblem` (the `blnGood`/`iChosen` loop in
 * `blockBoardEngine` and the equivalent one in `rulesBoardEngine`): after
 * picking the tile values for the top half of each equation, both engines
 * reshuffle that same set of values for the bottom-answer tiles and reject
 * any shuffle that leaves a value in the same position it started in
 * (which would make an equation trivially "solved" by an unmoved tile).
 *
 * @param values - The values to permute. Not mutated.
 * @param rng - A function returning a float in `[0, 1)`, used to drive the
 * shuffle.
 * @param maxAttempts - Safety cap on the number of shuffle attempts, to
 * avoid an unbounded loop for pathological inputs (e.g. a single-element or
 * all-identical-value array, which can never satisfy the no-fixed-point
 * constraint). Defaults to `1000`.
 * @returns A new array with the same elements as `values`, permuted so that
 * no element sits at its original index.
 * @throws {RangeError} If no valid permutation is found within
 * `maxAttempts` attempts.
 */
export function shuffleAvoidingFixedPositions<T>(
  values: readonly T[],
  rng: () => number,
  maxAttempts = 1000,
): T[] {
  if (values.length <= 1) {
    if (values.length === 1) {
      throw new RangeError(
        'Cannot shuffle a single-element array without a fixed point.',
      );
    }
    return [];
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const shuffled = values.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = shuffled[i]!;
      shuffled[i] = shuffled[j]!;
      shuffled[j] = tmp;
    }

    let hasFixedPoint = false;
    for (let i = 0; i < shuffled.length; i++) {
      if (shuffled[i] === values[i]) {
        hasFixedPoint = true;
        break;
      }
    }
    if (!hasFixedPoint) {
      return shuffled;
    }
  }

  throw new RangeError(
    `Could not find a shuffle without fixed positions within ${maxAttempts} attempts.`,
  );
}

/**
 * Splits a stacked tile entry on its `P` separator, e.g.
 * `'2 ÷ 2 = xP2 ÷ x = 2'`. `P` is a line break, not a fraction bar, so both
 * halves stay strings.
 *
 * @param topEQ - A raw dataset entry.
 * @returns `[topLine, bottomLine]`; `bottomLine` is `''` if there is no `P`.
 */
export function splitStackedEquation(topEQ: string): [string, string] {
  const [topLine = '', bottomLine = ''] = topEQ.split('P');
  return [topLine, bottomLine];
}
